// Capture policy (issue #3708): how the microphone is captured on each platform. One pure function
// maps the device to whether the browser is asked for its own echo cancellation and whether the
// app's own echo canceller runs on the captured signal. The Learn mic capture asks it once per mic
// open, so the exercises never see the difference between platforms.
//
// From the device research (docs/learn/audio-research.md, issue #3374): the iPhone's built-in
// canceller is full-duplex - it passes the singer's voice through while a guide plays - so iOS
// keeps the browser's cancellation on and needs no canceller of the app's own. No desktop browser
// has been measured, so desktop follows iOS until one is.
//
// Android captures the raw microphone and runs the app's own canceller (canceller/echo_canceller.ts),
// fed the exact guide the page plays: Samsung's platform canceller mutes or gates the singer while a
// guide sounds, and with the browser's cancellation off Android also stays in its normal audio mode,
// so the guide plays at media volume. The Learn mic capture streams the guide reference to the
// analysis worker and the canceller cleans each window there, ahead of the detectors.
// This is a bet for Android as a whole: the raw path with the app's own canceller was measured on a
// Galaxy A03s and a Galaxy A16 5G only (Chrome and the store WebView), and the issue's decision (#3708,
// "Capture policy") applies it to every Android device; other Android phones are unmeasured.

import { Device } from '../util/platform.ts';

export interface CapturePolicy {
  // What the browser is asked for in the getUserMedia constraints
  echoCancellation: boolean;
  // Whether the app's own echo canceller runs on the captured signal
  ownCanceller: boolean;
}

export function capturePolicyFor(device: Device): CapturePolicy {
  switch (device) {
    case Device.iOS:
      return { echoCancellation: true, ownCanceller: false };
    case Device.Android:
      return { echoCancellation: false, ownCanceller: true };
    case Device.Other:
      return { echoCancellation: true, ownCanceller: false };
  }
}
